import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Select } from '@nexora/design-system';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useFiscalInvoices } from '../fiscal/useFiscalInvoices';
import { usePaymentMethods } from '../finance/usePaymentMethods';
import { usePaymentTerms } from '../finance/usePaymentTerms';

/* Checkout de faturamento (protótipo erpone-checkout-faturamento).
 * Pedido: GET /orders?status=CONFIRMED e GET /orders/:id.
 * Forma e prazo: GET /finance/payment-methods e /finance/payment-terms.
 * Confirmar: PATCH /orders/:id/invoice com o id de uma NF já emitida
 * (GET /invoices?status=AUTHORIZED). O backend não emite boleto nem NF aqui.
 */

interface OrderItem {
  id: string;
  description: string;
  quantity: number;
  total: number;
  taxValues: Record<string, number>;
}

interface SalesOrder {
  id: string;
  number: string;
  customerName: string | null;
  customerId: string;
  status: string;
  items: OrderItem[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  paymentTerms: string;
  fiscalDocumentId: string | null;
  financial: { dueDate: string | null } | null;
}

interface OrderPage {
  data: SalesOrder[];
}

function installmentRows(total: number, count: number, intervalDays: number, firstDue: Date) {
  const n = Math.max(1, count);
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / n);
  const remainder = cents - base * n;
  return Array.from({ length: n }, (_, index) => {
    const due = new Date(firstDue);
    due.setDate(due.getDate() + intervalDays * index);
    const amount = (base + (index === n - 1 ? remainder : 0)) / 100;
    return { label: `${index + 1}/${n}`, due: due.toISOString(), amount };
  });
}

export function SalesCheckoutPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenant } = useAuth();
  const [orderId, setOrderId] = useState('');
  const [methodId, setMethodId] = useState('');
  const [termId, setTermId] = useState('');
  const [parcels, setParcels] = useState(1);
  const [documentId, setDocumentId] = useState('');
  const [message, setMessage] = useState('');

  const orders = useQuery({
    queryKey: ['sales', 'checkout', 'confirmed'],
    queryFn: () => apiClient.get<OrderPage>('/orders', { params: { status: 'CONFIRMED', page: '1', limit: '50' } }),
  });
  const detail = useQuery({
    queryKey: ['sales', 'checkout', 'order', orderId],
    queryFn: () => apiClient.get<SalesOrder>(`/orders/${orderId}`),
    enabled: orderId.length > 0,
  });
  const methods = usePaymentMethods({ isActive: true, pageSize: 50 });
  const terms = usePaymentTerms({ isActive: true, pageSize: 50 });
  const invoices = useFiscalInvoices(tenant?.id ?? '', { status: 'AUTHORIZED', page: 1, limit: 30 });

  const term = (terms.data?.data ?? []).find((item) => item.id === termId);
  const method = (methods.data?.data ?? []).find((item) => item.id === methodId);
  const order = detail.data;
  const maxParcels = Math.max(1, term?.maxInstallments ?? 1);
  const chosenParcels = Math.min(Math.max(1, parcels), maxParcels);
  const schedule = useMemo(() => {
    if (!order) return [];
    const start = order.financial?.dueDate ? new Date(order.financial.dueDate) : new Date();
    return installmentRows(order.grandTotal, chosenParcels, term?.installmentIntervalDays ?? 30, start);
  }, [order, chosenParcels, term?.installmentIntervalDays]);

  const invoice = useMutation({
    mutationFn: () => apiClient.patch<SalesOrder>(`/orders/${orderId}/invoice`, { fiscalDocumentId: documentId }),
    onSuccess: async () => {
      setMessage('Pedido faturado e vinculado à nota selecionada.');
      await queryClient.invalidateQueries({ queryKey: ['sales', 'checkout'] });
    },
    onError: (error: unknown) => {
      setMessage(error instanceof Error ? error.message : 'Não foi possível faturar o pedido.');
    },
  });

  return (
    <div className="flex flex-col gap-lg p-xl lg:flex-row">
      <div className="flex w-full shrink-0 flex-col gap-md lg:w-[380px]">
        <Card>
          <h2 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">Resumo do pedido</h2>
          <Select aria-label="Pedido confirmado" value={orderId} onChange={(e) => { setOrderId(e.target.value); setMessage(''); }}>
            <option value="">Selecione um pedido confirmado</option>
            {(orders.data?.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.number || item.id.slice(0, 8)} · {item.customerName ?? 'Cliente'} · {formatCurrency(item.grandTotal)}
              </option>
            ))}
          </Select>
          {orders.error ? <p className="mb-0 mt-sm font-body-sm text-body-sm text-error">{(orders.error as Error).message}</p> : null}
          {!orders.isLoading && (orders.data?.data.length ?? 0) === 0 ? (
            <p className="mb-0 mt-sm font-body-sm text-body-sm text-on-surface-variant">Nenhum pedido confirmado aguardando faturamento.</p>
          ) : null}
          {order ? (
            <>
              <p className="mb-xs mt-md font-body-sm text-body-sm text-on-surface-variant">
                Pedido {order.number || order.id} · {order.customerName ?? order.customerId}
              </p>
              <ul className="m-0 flex max-h-64 flex-col gap-xs overflow-auto p-0" style={{ listStyle: 'none' }}>
                {order.items.map((item) => (
                  <li key={item.id} className="flex justify-between gap-sm rounded-lg border border-outline-variant p-xs">
                    <span className="font-body-sm text-body-sm text-on-surface">
                      {item.description}
                      <span className="block text-on-surface-variant">Qtd {item.quantity}</span>
                    </span>
                    <span className="font-label-md text-label-md text-on-surface">{formatCurrency(item.total)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-sm flex flex-col gap-xs border-t border-outline-variant pt-sm font-body-sm text-body-sm text-on-surface-variant">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(order.subtotal)}</span></div>
                <div className="flex justify-between"><span>Desconto</span><span>{formatCurrency(order.discountTotal)}</span></div>
                <div className="flex justify-between"><span>Impostos</span><span>{formatCurrency(order.taxTotal)}</span></div>
                <div className="flex justify-between font-headline-sm text-headline-sm text-on-surface">
                  <span>Total</span><span>{formatCurrency(order.grandTotal)}</span>
                </div>
              </div>
            </>
          ) : null}
        </Card>
        <Card>
          <h3 className="mb-xs mt-0 font-label-md text-label-md text-on-surface">Cliente do pedido</h3>
          <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
            {order?.customerName ?? 'Selecione um pedido para ver o cliente.'}
          </p>
          {order?.paymentTerms ? (
            <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">Prazo no pedido: {order.paymentTerms}</p>
          ) : null}
        </Card>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-lg">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">Checkout (Faturamento)</h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Escolha a forma e o prazo cadastrados e vincule uma nota já autorizada.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-md xl:grid-cols-2">
          <Card>
            <h3 className="mb-sm mt-0 font-label-md text-label-md text-on-surface">Método de faturamento</h3>
            <Select aria-label="Forma de pagamento" value={methodId} onChange={(e) => setMethodId(e.target.value)}>
              <option value="">Selecione</option>
              {(methods.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>{item.name} · {item.type}</option>
              ))}
            </Select>
            {method ? (
              <p className="mb-0 mt-sm font-body-sm text-body-sm text-on-surface-variant">
                Liquidação em {method.settlementDays} dia{method.settlementDays === 1 ? '' : 's'}. A emissão de boleto não acontece nesta tela.
              </p>
            ) : null}
          </Card>
          <Card>
            <h3 className="mb-sm mt-0 font-label-md text-label-md text-on-surface">Condição de pagamento</h3>
            <Select
              aria-label="Prazo"
              value={termId}
              onChange={(e) => {
                setTermId(e.target.value);
                const next = (terms.data?.data ?? []).find((item) => item.id === e.target.value);
                setParcels(Math.max(1, next?.maxInstallments ?? 1));
              }}
            >
              <option value="">À vista, no vencimento do pedido</option>
              {(terms.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · até {item.maxInstallments}x a cada {item.installmentIntervalDays} dias
                </option>
              ))}
            </Select>
            {maxParcels > 1 ? (
              <Select aria-label="Parcelas" value={String(chosenParcels)} onChange={(e) => setParcels(Number(e.target.value))}>
                {Array.from({ length: maxParcels }, (_, index) => (
                  <option key={index + 1} value={String(index + 1)}>{index + 1}x</option>
                ))}
              </Select>
            ) : null}
            <table className="mt-sm w-full text-left">
              <thead>
                <tr className="font-label-md text-label-md text-on-surface-variant">
                  <th className="py-xs">Parcela</th>
                  <th className="py-xs">Vencimento</th>
                  <th className="py-xs text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row) => (
                  <tr key={row.label} className="border-t border-outline-variant font-body-sm text-body-sm text-on-surface">
                    <td className="py-xs">{row.label}</td>
                    <td className="py-xs">{formatDate(row.due)}</td>
                    <td className="py-xs text-right">{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
              Simulação do prazo escolhido sobre o total do pedido. As parcelas não são gravadas.
            </p>
          </Card>
        </div>

        <Card>
          <h3 className="mb-sm mt-0 font-label-md text-label-md text-on-surface">Nota fiscal autorizada</h3>
          <Select aria-label="Documento fiscal" value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
            <option value="">Selecione a NF já emitida</option>
            {(invoices.data?.data ?? []).map((invoiceItem) => (
              <option key={invoiceItem.id} value={invoiceItem.id}>
                {invoiceItem.type} · {invoiceItem.destinatary?.name ?? 'Destinatário'} · {formatCurrency(invoiceItem.totals?.totalValue ?? 0)}
              </option>
            ))}
          </Select>
          {message ? <p className="mb-0 mt-sm font-body-sm text-body-sm text-on-surface">{message}</p> : null}
          <div className="mt-md flex flex-wrap justify-end gap-sm">
            <Button variant="ghost" onClick={() => void navigate({ to: '/sales' })}>Cancelar</Button>
            <Button
              variant="outline"
              onClick={() => {
                if (documentId) {
                  void navigate({ to: '/fiscal/invoices/$id', params: { id: documentId } });
                  return;
                }
                void navigate({ to: '/fiscal/invoices/new' });
              }}
            >
              Pré-visualizar NF
            </Button>
            <Button
              variant="primary"
              disabled={!orderId || !documentId || invoice.isPending || order?.status !== 'CONFIRMED'}
              loading={invoice.isPending}
              onClick={() => invoice.mutate()}
            >
              Confirmar faturamento
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
